import { useEffect, useState } from 'react';
import { signoffReject, signoffRelease, signoffSign, signoffsList, type SignoffItem } from '@aithora/shared';
import { useSession } from '../../session/SessionContext';
import { useQuery } from '../../useQuery';
import { useReadOnlyReason } from '../../ui/ReadOnly';
import { ConfirmDialog } from '../../ui/ConfirmDialog';
import { sx } from '../../ui/designStyles';

// The two-signature approvals list (Andrew, 2026-09-30), ported from the design's
// approvals card (iApprovals, template L4800-4874): the action, N of M signatures with
// the signers, the clock, Atlas's read when the action has one, a stated reason on every
// high-severity signature, and Approve & sign / Reject. The building's clinicians sign;
// Aithora reads. Drawn only while something is on the list.
//
// Atlas's everyday approval queue ("Needs your OK") lives in the Atlas panel, with its count
// in the top bar, as in the V6 design; it is no longer repeated here (Andrew, 2026-10-13:
// one home for each kind of approval). This section is the clinical two-signature list only.

const POLL_MS = 15_000;
const BTN = 'display:inline-flex;align-items:center;justify-content:center;height:32px;padding:0 14px;border-radius:7px;font-size:14px;font-weight:600;text-decoration:none;white-space:nowrap;';

const mmss = (ms: number) => {
  const s = Math.max(0, Math.floor(ms / 1000));
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
};

function clockText(a: SignoffItem, now: number): { text: string; color: string } {
  if (a.status === 'approved') return { text: 'threshold met · ready', color: '#1F6B52' };
  if (a.status === 'rejected') return { text: 'rejected · blocked', color: '#B3261E' };
  if (a.status === 'expired') return { text: 'expired unsigned · blocked', color: '#B3261E' };
  if (a.clock.ladderExhausted) return { text: 'unsigned · blocked until signed', color: '#B3261E' };
  const left = a.clock.expiresAt ? Date.parse(a.clock.expiresAt) - now : 0;
  const esc = a.clock.escalated && a.clock.stepLabel ? ` · escalated to ${a.clock.stepLabel.toLowerCase()}` : '';
  return { text: `expires ${mmss(left)}${esc}`, color: a.clock.escalated ? '#B3261E' : '#5B6B7C' };
}

function confStyle(c: number): string {
  return 'display:inline-flex;align-items:center;height:20px;padding:0 9px;border-radius:5px;font-variant-numeric:tabular-nums;font-size:14px;font-weight:700;letter-spacing:.06em;white-space:nowrap;color:'
    + (c >= 0.8 ? '#1F6B52;background:rgba(47,138,111,.16);' : c >= 0.6 ? '#5B6B7C;background:rgba(91,107,124,.18);' : '#B3261E;background:rgba(179,38,30,.14);');
}

export function GuardianApprovals() {
  const { client } = useSession();
  const roReason = useReadOnlyReason();
  const q = useQuery((signal) => signoffsList(client, { signal }).catch(() => null), [client], { pollMs: POLL_MS });
  const [now, setNow] = useState(() => Date.now());
  const [reasons, setReasons] = useState<Record<string, string>>({});
  const [rejectFor, setRejectFor] = useState<SignoffItem | null>(null);
  const [err, setErr] = useState<string | null>(null);
  useEffect(() => {
    const t = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(t);
  }, []);
  const items = q.data?.items ?? [];
  if (!items.length) return null;

  const sign = async (a: SignoffItem) => {
    setErr(null);
    try {
      await signoffSign(client, { params: { id: a.id }, body: { reason: reasons[a.id]?.trim() || undefined } });
      q.reload();
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'The signature was not recorded.');
    }
  };
  const collect = async (a: SignoffItem) => {
    setErr(null);
    try {
      const r = await signoffRelease(client, { params: { id: a.id } });
      const blob = new Blob([JSON.stringify(r.bundle, null, 2)], { type: 'application/fhir+json' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `fhir-bundle-${a.id.slice(0, 8)}.json`;
      link.click();
      URL.revokeObjectURL(url);
      q.reload();
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'It could not be collected.');
    }
  };

  return (
    <section aria-label="Approvals" data-testid="guardian-approvals" style={sx('flex:0 0 auto;display:flex;flex-direction:column;gap:12px;min-width:0;')}>
      <div style={sx('display:flex;align-items:baseline;gap:9px;flex-wrap:wrap;row-gap:6px;padding:0 2px 2px;')}>
        <span style={sx('font-size:16px;font-weight:700;color:#22272E;')}>Article 14(5) two-person verification</span>
        <span style={sx('font-size:14px;color:#54606C;line-height:1.5;')}>
          No action is taken on the system’s identification until two or more distinct people with the authority to do so have separately confirmed it. The G3 kill switch is deliberately exempt: stopping the system is one click, no counter-signature.
        </span>
      </div>
      {q.data?.signerNote && <div style={sx('font-size:14px;color:#54606C;padding:0 2px;')}>{q.data.signerNote}</div>}
      {err && <div role="alert" style={sx('font-size:14px;color:#B3261E;padding:0 2px;')}>{err}</div>}
      {items.map((a) => {
        const c = clockText(a, now);
        const reason = reasons[a.id] ?? '';
        const reasonBlocked = a.needsReason && !reason.trim() && !a.mine;
        const canSign = a.canSign && !roReason;
        const signLabel = a.mine ? 'Signed by you' : reasonBlocked && a.canSign ? 'Reason required' : 'Approve & sign';
        const signStyle = BTN + (a.mine ? 'background:rgba(47,138,111,.13);color:#1F6B52;pointer-events:none;'
          : !canSign || reasonBlocked ? 'background:rgba(34,39,46,.06);color:#54606C;border:1px solid rgba(34,39,46,.12);pointer-events:none;'
            : 'background:#2F5C97;color:#fff;');
        const left = a.need - a.have;
        const note = a.status === 'approved'
          ? (a.canCollect ? 'Threshold met. Sealed to the Truth Ledger.' : 'Threshold met. Sealed to the Truth Ledger.')
          : a.status !== 'pending' ? (a.rejectedBy?.reason ? `Rejected: ${a.rejectedBy.reason}` : 'Blocked.')
            : a.mine ? `Awaiting ${left} more signature${left === 1 ? '' : 's'}.`
              : a.isRequester ? 'You asked for this, so two other clinicians sign.'
                : a.canSign ? 'Your signature is sealed to the Truth Ledger with your name and the time.'
                  : q.data?.canSign ? '' : 'The building’s clinicians sign.';
        return (
          <div key={a.id} data-testid={`signoff-${a.id}`} style={sx('border-top:1px solid rgba(34,39,46,.14);padding:14px 16px;min-width:0;')}>
            <div style={sx('display:flex;align-items:center;gap:10px;flex-wrap:wrap;row-gap:6px;')}>
              <span style={sx('font-size:14px;font-weight:700;')}>{a.act}</span>
              <span style={sx('flex:1;')} />
              <span style={sx(`font-variant-numeric:tabular-nums;font-size:14px;font-weight:700;white-space:nowrap;color:${c.color};`)}>{c.text}</span>
            </div>
            <div style={sx('display:flex;align-items:center;gap:9px;flex-wrap:wrap;row-gap:6px;margin-top:10px;')}>
              <span style={sx('display:inline-flex;align-items:center;gap:5px;')}>
                {Array.from({ length: a.need }, (_, i) => (
                  <span key={i} style={sx(`width:8px;height:8px;border-radius:50%;flex:0 0 8px;background:${i < a.have ? '#2F5C97' : 'rgba(34,39,46,.16)'};`)} />
                ))}
              </span>
              <span style={sx('font-size:14px;font-weight:600;color:#54606C;')}>{`${a.have} of ${a.need} signatures`}</span>
              {a.signers.length > 0 && (
                <span style={sx('font-size:14px;color:#54606C;')}>{`Signed: ${a.signers.map((s) => [s.name, s.title].filter(Boolean).join(', ')).join(' · ')}`}</span>
              )}
              <span style={sx('flex:1 1 130px;min-width:min(100%,130px);')} />
              {a.read?.confidence != null && <span style={sx(confStyle(a.read.confidence))}>{`${Math.round(a.read.confidence * 100)}% confident`}</span>}
            </div>
            {a.read && (a.read.forIt || a.read.against) && (
              <div style={sx('display:grid;grid-template-columns:repeat(auto-fit,minmax(220px,1fr));gap:10px;margin-top:11px;')}>
                <div style={sx('box-sizing:border-box;background:rgba(47,138,111,.07);border:1px solid rgba(47,138,111,.26);border-radius:9px;padding:9px 11px;')}>
                  <div style={sx('font-size:14px;letter-spacing:.09em;text-transform:uppercase;white-space:nowrap;color:#1F6B52;font-weight:700;')}>Evidence for</div>
                  <div style={sx('font-size:14px;color:#3C454F;margin-top:3px;line-height:1.5;text-wrap:pretty;')}>{a.read.forIt ?? 'None given.'}</div>
                </div>
                <div style={sx('box-sizing:border-box;background:rgba(179,38,30,.06);border:1px solid rgba(179,38,30,.28);border-radius:9px;padding:9px 11px;')}>
                  <div style={sx('font-size:14px;letter-spacing:.09em;text-transform:uppercase;white-space:nowrap;color:#B3261E;font-weight:700;')}>Evidence against</div>
                  <div style={sx('font-size:14px;color:#3C454F;margin-top:3px;line-height:1.5;text-wrap:pretty;')}>{a.read.against ?? 'None given.'}</div>
                </div>
              </div>
            )}
            {a.needsReason && a.canSign && !a.mine && (
              <div style={sx('margin-top:11px;')}>
                <input
                  type="text"
                  aria-label="Why is this safe to approve?"
                  value={reason}
                  onChange={(e) => setReasons({ ...reasons, [a.id]: e.target.value })}
                  placeholder="Why is this safe to approve? (required)"
                  style={sx('box-sizing:border-box;width:100%;height:26px;padding:0 12px;border:1px solid rgba(34,39,46,.18);border-radius:8px;background:#FBF8F1;font-size:16px;color:#22272E;font-family:inherit;')}
                />
                {reasonBlocked && (
                  <div style={sx('font-size:14px;color:#5B6B7C;margin-top:6px;line-height:1.5;text-wrap:pretty;')}>
                    A high-severity approval needs a stated reason - it is the same record the Residents loop writes, and it is what makes an approval reviewable later.
                  </div>
                )}
              </div>
            )}
            <div style={sx('display:flex;align-items:center;gap:9px;flex-wrap:wrap;row-gap:6px;margin-top:11px;')}>
              {a.status === 'pending' && (
                <>
                  <a href="#" aria-disabled={!canSign || reasonBlocked || undefined} title={roReason ?? undefined} onClick={(e) => { e.preventDefault(); if (canSign && !reasonBlocked) void sign(a); }} style={sx(signStyle)}>
                    {signLabel}
                  </a>
                  {canSign && !a.mine && (
                    <a href="#" onClick={(e) => { e.preventDefault(); setRejectFor(a); }} style={sx(BTN + 'border:1px solid rgba(34,39,46,.16);color:#54606C;')}>
                      Reject
                    </a>
                  )}
                </>
              )}
              {a.canCollect && (
                <a href="#" onClick={(e) => { e.preventDefault(); void collect(a); }} style={sx(BTN + 'background:#2F5C97;color:#fff;')}>
                  Collect the export
                </a>
              )}
              <span style={sx('flex:1;')} />
              <span style={sx('font-size:14px;color:#54606C;')}>{note}</span>
            </div>
          </div>
        );
      })}
      {rejectFor && (
        <ConfirmDialog
          title="Reject this action?"
          body={`${rejectFor.act}. One rejection stops it; it is sealed to the Truth Ledger with your name and the time.`}
          confirmLabel="Reject"
          reason={{ label: 'Why', placeholder: 'Why it should not go ahead (required)', required: true }}
          onConfirm={async (why) => {
            await signoffReject(client, { params: { id: rejectFor.id }, body: { reason: why } });
            q.reload();
          }}
          onClose={() => setRejectFor(null)}
        />
      )}
    </section>
  );
}
